import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { tenantApi } from '../api/endpoints';
import type { PersonSettings } from '../api/types';
import { useI18n } from '../i18n';
import { useToast } from '../ui';
import { setCompanyZone, setFixedZone } from './viewer-zone';
import { deviceZone, zoneCity } from './zones';

// The person's zone, kept with their account (TF-220), applied in the app: a zone fixed in My account,
// or Automatic - the device's, reported to the server so e-mails and reminders follow it. When the
// device's zone changes (travel), the app follows at the next visit and says so once.

export const PERSON_SETTINGS_KEY = ['person-settings'] as const;

export function useTimeZoneSync(company: { timeZone?: string } | null | undefined): void {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: PERSON_SETTINGS_KEY, queryFn: tenantApi.mySettings, staleTime: 10 * 60_000 });
  const reported = useRef(false);

  useEffect(() => setCompanyZone(company?.timeZone ?? null), [company?.timeZone]);

  useEffect(() => {
    const mine = settings.data;
    if (!mine) return;
    setFixedZone(mine.timeZone);
    const device = deviceZone();
    if (reported.current || mine.deviceZone === device) return;
    reported.current = true;
    void tenantApi
      .saveMySettings({ deviceZone: device })
      .then((saved) => queryClient.setQueryData<PersonSettings>(PERSON_SETTINGS_KEY, saved))
      .catch(() => undefined);
    // Moved on Automatic: times follow the device from now on, and the person is told where to fix it.
    if (mine.timeZone === null && mine.deviceZone !== null) {
      toast.info(t('time.nowShownIn', { city: zoneCity(device) }), { action: { label: t('time.change'), onAction: () => void navigate('/account#time-zone') } });
    }
  }, [settings.data]);
}
